/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `appointmentRegistrationUtils` for the Caintra Connect frontend.
 * @module utils/appointmentRegistrationUtils
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { parseCertificationOptionRequirementKey } from "./common.js";

export const CORPORATE_IDENTITY_FIELDS = [
  "nombreComercial",
  "razonSocial",
  "sectorEconomico",
  "ramaSCIAN",
  "subSectorSCIAN",
  "grupoEmpresarial",
  "descripcionDeLaEmpresa",
  "logo",
  "actividadGeneralDeLaEmpresa",
  "ambitoOperaciones",
  "tamanoDeLaEmpresa",
];

export const CONTACT_INFORMATION_FIELDS = [
  "telefonoDeOficina",
  "correoElectronicoPrincipal",
  "paginaInternet",
];

export const LOCATION_FIELDS = [
  "domicilio2Pais",
  "domicilio2Estado",
  "domicilio2Municipio",
];

export const COMMERCIAL_OFFER_FIELDS = [
  "exporta",
  "paisExportacion",
  "importa",
  "paisImportacion",
  "vendeAGobierno",
];

export const DOCUMENTS_FIELDS = [
  "brochure",
  "cedulaFiscal",
  "opinionDeCumplimientoPositiva",
  "polizaDeResponsabilidadCivil",
  "altaIMSS",
  "REPSE",
  "comprobanteDeCertificacion",
];

export const CERTIFICATIONS_FIELDS = [
  "certificaciones",
  "sectores",
  "tieneSistemaDocumentado",
  "capacidadInstalada",
  "esProveedorREPSE",
  "tiemposPromedioEntrega",
  "metodosDePagoAceptados",
  "seguroDeResponsabilidadCivil",
  "permisoIMMEX",
];

/** Split an array into consecutive chunks of the given size. */
export const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let index = 0; index < array.length; index += chunkSize) {
    chunks.push(array.slice(index, index + chunkSize));
  }
  return chunks;
};

/** Returns true when the company object includes at least one of the given field keys. */
export const companyHasAnyField = (company, fieldKeys) => {
  if (!company) {
    return false;
  }
  return fieldKeys.some((field) => Object.hasOwn(company, field));
};

/** Stable remount key for profile-aware registration sections. */
export const buildRegistrationSectionKey = (prefix, company, selectedProfile) =>
  `${prefix}-${company?._lastUpdate || ""}-${selectedProfile}`;

/** True when any entry in the missing-fields map is still required. */
export const hasMissingRequiredFields = (missingRequiredFields) =>
  Boolean(
    missingRequiredFields &&
      Object.values(missingRequiredFields).some((value) => value === true),
  );

/** Sync certification checkbox selections with missing required/optional field maps. */
export const syncCertificationMissingFields = (value, setMissingFields) => {
  if (!Array.isArray(value)) {
    return;
  }

  setMissingFields((prev) => {
    if (!prev) {
      return prev;
    }
    let next = null;
    for (const key of Object.keys(prev)) {
      const idx = parseCertificationOptionRequirementKey(key);
      if (idx === null) {
        continue;
      }
      const missing = !value.includes(idx);
      if (prev[key] !== missing) {
        if (!next) {
          next = { ...prev };
        }
        next[key] = missing;
      }
    }
    return next ?? prev;
  });
};
